import { AppShell, Menu, Text, UnstyledButton } from "@mantine/core";
import { useLocale, useChangeLocale, useTenant, AdminHeader, DEFAULT_THEME } from "@dehub/theme";
import { IconChevronDown, IconLanguage } from "@tabler/icons-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { AuthApi, TenantApi } from "../../apis";
import { NavigationItem } from "../../constants/navigation";
import useUser from "../../hooks/user";
import { useTenantAdminTid } from "../../hooks/useIsTenantAdmin";
import { authChange, authMe } from "../../store/Auth";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { renderName } from "../../utils/render-name";

export const Header = ({ section }: { section?: NavigationItem; toggled?: boolean }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const dispatch = useDispatch();
  const tenant = useTenant();

  // A tenant-admin (token carries `tid`) should see THEIR tenant's logo/name.
  // Fetch the bound tenant by id and override the header branding (logo/name
  // only; colors are always the fixed DeHub palette — the back-office never
  // adopts a tenant brand color).
  const tid = useTenantAdminTid();
  const { data: ownTenant } = useSWR(
    tid ? `swr.tenant-admin.brand.${tid}` : null,
    () => TenantApi.get(tid!)
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const brand = (tid && ownTenant ? ownTenant : tenant) as any;

  const onLogout = async () => {
    try {
      await AuthApi.logout();
      dispatch(authChange({ accessToken: null } as any));
      dispatch(authMe({} as any));
      navigate("/auth/login");
    } catch (error: any) {
      Message.error((error as HttpHandler).message!);
    }
  };

  const userName = renderName(user) || "";
  const userEmail = user?.email || "";
  const userInitial = (userName.trim().charAt(0) || "U").toUpperCase();

  return (
    <AppShell.Header>
      <AdminHeader
        tenant={{
          name: brand?.name || "",
          logo: brand?.logo || null,
          domain: brand?.domain || "",
        }}
        pageTitle={section?.headerTitle || section?.title || ""}
        headerGradient={DEFAULT_THEME.theme.tokens.header.gradient}
        headerTextColor={DEFAULT_THEME.theme.tokens.header.textColor}
        userName={userName}
        userEmail={userEmail}
        userInitial={userInitial}
        onProfile={() => navigate("/settings/profile")}
        onLogout={onLogout}
        localeSlot={<LocalLanguageSwitcher />}
      />
    </AppShell.Header>
  );
};

function LocalLanguageSwitcher() {
  const { locale, supportedLocales, isMultiLang } = useLocale();
  const changeLocale = useChangeLocale();
  if (!isMultiLang) return null;

  const FLAG: Record<string, string> = { mn: "🇲🇳", ko: "🇰🇷", en: "🇺🇸", zh: "🇨🇳" };
  return (
    <Menu shadow="xl" width={180} position="bottom-end" radius="lg">
      <Menu.Target>
        <UnstyledButton
          style={{
            height: 36, padding: "0 10px", borderRadius: 10,
            display: "flex", alignItems: "center", gap: 6,
            color: "inherit", background: "transparent",
            transition: "background 120ms ease",
          }}
        >
          <IconLanguage size={16} stroke={1.75} />
          <Text component="span" size="xs" fw={700} style={{ letterSpacing: "0.04em", textTransform: "uppercase", color: "inherit" }}>
            {locale}
          </Text>
          <IconChevronDown size={12} stroke={1.75} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {supportedLocales.map((loc) => (
          <Menu.Item
            key={loc}
            onClick={() => changeLocale(loc)}
            leftSection={<Text size="sm">{FLAG[loc] || loc}</Text>}
            fw={loc === locale ? 700 : 500}
            c={loc === locale ? "brand" : "dark.6"}
          >
            {{ mn: "Монгол", ko: "한국어", en: "English", zh: "中文" }[loc] || loc}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
